import { Component, Input } from '@angular/core'
import { Customer, PlanAddOn } from '../types'
import { SubscriptionActions } from '../actions/subscription-actions'
import { customerInContext, CContext } from 'src/logic/customer-states'
import { fullDayWithTime, isVideoPlanCode } from 'src/utils'
import get from 'lodash-es/get'
import { User } from 'src/pages/dashboard-page/dashboard.queries'
import sweetAlert from 'src/bmAlert'
import { UserMutations } from 'src/graphql/mutations/user.mutations'

@Component({
  selector: 'subscription-header',
  styles: [
    `
      :host {
        display: block;
      }
      .subscription-options {
        padding: 3px 23px;
      }

      .btn-group.open .dropdown-toggle {
        box-shadow: none;
        -webkit-box-shadow: none;
        -moz-box-shadow: none;
      }

      .dropdown-menu > li > a {
        font-size: 16px;
        padding: 10px 20px;
      }

      .header-container {
        text-align: center;
      }
      .header {
        margin-bottom: 5px;
      }
      .subheader {
        margin-top: 0;
        color: darkgray;
      }
      .migration-alert {
        background: #fcffd7;
        color: #8f963f;
        border-radius: 4px;
      }

      @media (max-width: 421px) {
        .subscription-options,
        .posts-btn {
          padding: 0;
          border: 0px;
        }
      }
    `,
  ],
  template: `
    <p
      *ngIf="currentUser.showMigrationBanner"
      class="text-center fs-5 px-4 py-2 my-3 migration-alert">
      <span>Do things look new around here? Yes they do!</span>
      <a href="javascript:void(0)" (click)="openMigrationModal()">Learn more</a>
    </p>
    <div class="header-container" *ngIf="customer || header || subheader">
      <h4 class="header text-no-transform" [class.my-5]="isWhiteLabel">
        {{ isWhiteLabel ? 'Content for' : '' }}
        {{ header ? header : customerName() }}
        <a
          *ngIf="!isWhiteLabel && valetCustomer"
          class="link cursor-pointer"
          [popover]="iconPopoverCopy"
          container="body"
          [placement]="'bottom'"
          triggers="mouseenter:mouseleave">
          <i class="fas fa-bolt multipass"></i>
        </a>
        <i
          *ngIf="!isWhiteLabel && customer.subscription?.scheduledToCancelAt"
          class="fas fa-ban multipass ml-1"></i>
      </h4>

      <p class="subtitle-body thin mb-0">
        <span *ngIf="subheader">{{ subheader }}</span>
        <ng-template [ngIf]="!subheader && currentUser?.canUpdateCustomer">
          <span *ngIf="!customer.subscription && customer.state == 'active'"
            >loading...</span
          >

          <span *ngIf="customer.state == 'pending'">Incomplete</span>
          <span *ngIf="customer.state == 'expired'">Expired</span>

          <ng-template [ngIf]="inContext('current')">
            <span *ngIf="customer.subscription">{{ planName() }}</span>
            <span *ngIf="willCancelAt()">, {{ willCancelAt() }}</span>
            <span *ngIf="!videoPlan && customer.subscription?.plan?.words"
              >, {{ customer.subscription?.plan?.words }} words
            </span>
            <span *ngIf="customer.postsIncluded && !customer.multipass"
              >, {{ customer.postsIncluded }} monthly</span
            >
          </ng-template>

          <span
            *ngIf="
              customer.subscription?.plan?.words && customer.state == 'canceled'
            "
            >,
          </span>
          <span *ngIf="customer.state == 'canceled'">, Canceled</span>
        </ng-template>
      </p>
      <customer-credits
        *ngIf="canViewCredits"
        [customer]="customer"></customer-credits>
    </div>
  `,
})
export class SubscriptionHeader {
  @Input() customer: Customer
  @Input() currentUser: User
  @Input() header: string = null
  @Input() subheader: string = null

  constructor(
    private subscriptionActions: SubscriptionActions,
    private userMutations: UserMutations
  ) {}

  ngOnInit(): void {
    if (
      this.currentUser.showMigrationBanner &&
      this.currentUser.uiPreferences.showMigrationAlertOnLoad
    ) {
      this.openMigrationModal()
    }
  }

  ngOnChanges(changeRecord: any): void {
    if (
      changeRecord.customer &&
      this.customer &&
      !this.customer.subscription &&
      this.customer.id
    ) {
      this.subscriptionActions.loadSubscription(this.customer.id)
    }
  }

  get isWhiteLabel(): boolean {
    return this.currentUser && this.currentUser.role === 'white_label'
  }

  get canViewCredits(): boolean {
    const customerState = this.customer.state
    return (
      customerState !== 'pending' &&
      customerState !== 'expired' &&
      !this.isWhiteLabel
    )
  }

  customerName(): string {
    return this.isWhiteLabel
      ? this.customer.displayName
      : this.customer.businessName
  }

  postAddOns(): PlanAddOn[] {
    return get(this.customer, 'subscription.planAddOns', []).filter((ao) =>
      ['option-a', 'option-b', 'option-c', 'option-h'].includes(ao.code)
    )
  }

  planName(): string {
    let name

    if (this.customer.multipass) {
      name = 'Content, Any length (beta)'
    } else {
      if (this.postAddOns().find((a) => a.code === 'option-a'))
        name = '+Optimize'
      else if (this.postAddOns().find((a) => a.code === 'option-b'))
        name = '+Photo'
      else if (this.postAddOns().find((a) => a.code === 'option-c'))
        name = 'Complete'
      else if (this.videoPlan) name = 'Video'
      else name = 'Content'
      if (this.postAddOns().find((a) => a.code === 'option-h'))
        name += ' and +Video'
    }

    return name
  }

  inContext(context: CContext): boolean {
    return this.customer && customerInContext(this.customer, context)
  }

  get videoPlan(): boolean {
    return isVideoPlanCode(get(this.customer, 'subscription.plan.code', ''))
  }

  get valetCustomer(): boolean {
    return this.customer.subscription?.selectedProducts?.some(
      (p) => p.code === 'valet'
    )
  }

  willCancelAt(): string {
    return (
      this.customer.subscription.scheduledToCancelAt &&
      `Scheduled to cancel at ${fullDayWithTime(
        this.customer.subscription.scheduledToCancelAt,
        true
      )}`
    )
  }

  get iconPopoverCopy(): string {
    const endDate = new Date(this.customer.subscription.nextCreditsIssuedAt)
    return `Managed by Verblio Valet until ${fullDayWithTime(endDate, true)}`
  }

  openMigrationModal(): void {
    sweetAlert.migrationCandidateAlert().then((result) => {
      if (result.value)
        window.open(
          'https://help.verblio.com/en/articles/6732679-flexible-credit-system',
          '_blank'
        )
      if (this.currentUser.uiPreferences.showMigrationAlertOnLoad) {
        this.userMutations.updateUserPreferences({
          showMigrationAlertOnLoad: false,
        })
      }
    })
  }
}
